import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ShoppingCart, Phone, Moon, Sun } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/lib/ThemeContext";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { dark, toggle } = useTheme();

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 border-b ${scrolled ? "border-border/20 backdrop-blur-md bg-background/80" : "border-transparent"}`}
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" aria-label="Water Slides Houston Texas - Home" className="flex items-center gap-2 flex-shrink-0 group">
          <img
            src="https://media.base44.com/images/public/6a2225f85d295b30c327a9d5/8e977dff1_svg_001.svg"
            alt="Water Slides Houston Texas"
            className="h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <span className="font-extrabold text-primary text-xl hidden tracking-tight" style={{ display: "none" }}></span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden sm:flex items-center gap-1.5">
          <Link
            to="/"
            className={`px-3 py-2 rounded-full transition-all duration-200 ${
              isActive("/") 
                ? "bg-primary/10" 
                : "hover:bg-primary/5"
            }`}
            style={{ color: dark ? "#ffffff" : "#0e3a66", fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: "1.1em" }}
          >
            Home
          </Link>
          <Link
            to="/water-slides-rentals"
            className={`px-3 py-2 rounded-full transition-all duration-200 ${
              isActive("/water-slides-rentals") 
                ? "bg-primary/10" 
                : "hover:bg-primary/5"
            }`}
            style={{ color: dark ? "#ffffff" : "#0e3a66", fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: "1.1em" }}
          >
            Water Slides
          </Link>
          <Link
            to="/party-rentals"
            className={`px-3 py-2 rounded-full transition-all duration-200 ${
              isActive("/party-rentals") 
                ? "bg-primary/10" 
                : "hover:bg-primary/5"
            }`}
            style={{ color: dark ? "#ffffff" : "#0e3a66", fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: "1.1em" }}
          >
            Party Rentals
          </Link>
          <Link
            to="/browse-rentals"
            className={`px-3 py-2 rounded-full transition-all duration-200 ${
              isActive("/browse-rentals") 
                ? "bg-primary/10" 
                : "hover:bg-primary/5"
            }`}
            style={{ color: dark ? "#ffffff" : "#0e3a66", fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: "1.1em" }}
          >
            Browse Rentals
          </Link>
          <Link
            to="/service-area"
            className={`px-3 py-2 rounded-full transition-all duration-200 ${
              isActive("/service-area") 
                ? "bg-primary/10" 
                : "hover:bg-primary/5"
            }`}
            style={{ color: dark ? "#ffffff" : "#0e3a66", fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: "1.1em" }}
          >
            Service Area
          </Link>
          <Link to="/about-us" className={`px-3 py-2 rounded-full transition-all duration-200 ${isActive("/about-us") ? "bg-primary/10" : "hover:bg-primary/5"}`} style={{ color: dark ? "#ffffff" : "#0e3a66", fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: "1.1em" }}>About Us</Link>
          <a
            href="tel:18329704583"
            className="flex items-center gap-1.5 px-4 py-2 rounded-full font-bold text-primary bg-primary/10 hover:bg-primary hover:text-primary-foreground transition-all duration-300 ml-2"
            style={{ fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: "1.1em" }}
          >
            <Phone className="w-5 h-5" />
            Call: 832 970 4583
          </a>

          <button
            onClick={toggle}
            className="p-2 rounded-full hover:bg-primary/10 transition-colors ml-1"
            aria-label="Toggle dark mode"
          >
            {dark ? <Sun className="w-6 h-6 text-primary" /> : <Moon className="w-6 h-6 text-foreground" />}
          </button>

          <a href="#" className="flex items-center gap-1.5 px-4 py-2 rounded-full font-bold text-foreground hover:text-primary hover:bg-primary/10 transition-all duration-300 ml-1" style={{ fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: "1.1em" }}>
            <span className="hidden lg:inline">Cart</span>
            <ShoppingCart className="w-6 h-6" />
          </a>
        </nav>

        {/* Mobile: Cart + Menu */}
        <div className="flex items-center gap-3 sm:hidden">
          <a href="#" aria-label="Shopping cart" className="p-2 rounded-full hover:bg-muted transition-colors relative">
            <ShoppingCart className="w-5 h-5 text-foreground" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full"></span>
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full hover:bg-muted w-12 h-12" aria-label="Open menu">
                <Menu className="w-7 h-7 text-foreground" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-background border-l border-border p-6">
              <div className="flex items-center justify-between mb-8">
                <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-2">
                  <img
                    src="https://media.base44.com/images/public/6a2225f85d295b30c327a9d5/8e977dff1_svg_001.svg"
                    alt="Water Slides Houston Texas"
                    className="h-10 w-auto object-contain"
                    onError={(e) => { e.target.style.display="none"; e.target.nextElementSibling.style.display="inline"; }}
                  />
                  <span className="font-extrabold text-primary text-lg tracking-tight" style={{ display: "none" }}>Water Slides</span>
                </Link>
              </div>
              <nav className="flex flex-col gap-2">
              <Link to="/" onClick={() => setOpen(false)} className={`px-4 py-3 rounded-xl font-bold text-base transition-all ${isActive("/") ? "bg-primary/10 text-primary" : dark ? "text-white hover:bg-muted" : "text-foreground hover:bg-muted"}`}>Home</Link>
              <Link to="/water-slides-rentals" onClick={() => setOpen(false)} className={`px-4 py-3 rounded-xl font-bold text-base transition-all ${isActive("/water-slides-rentals") ? "bg-primary/10 text-primary" : dark ? "text-white hover:bg-muted" : "text-foreground hover:bg-muted"}`}>Water Slides</Link>
              <Link to="/party-rentals" onClick={() => setOpen(false)} className={`px-4 py-3 rounded-xl font-bold text-base transition-all ${isActive("/party-rentals") ? "bg-primary/10 text-primary" : dark ? "text-white hover:bg-muted" : "text-foreground hover:bg-muted"}`}>Party Rentals</Link>
              <Link to="/browse-rentals" onClick={() => setOpen(false)} className={`px-4 py-3 rounded-xl font-bold text-base transition-all ${isActive("/browse-rentals") ? "bg-primary/10 text-primary" : dark ? "text-white hover:bg-muted" : "text-foreground hover:bg-muted"}`}>Browse Rentals</Link>
              <Link to="/service-area" onClick={() => setOpen(false)} className={`px-4 py-3 rounded-xl font-bold text-base transition-all ${isActive("/service-area") ? "bg-primary/10 text-primary" : dark ? "text-white hover:bg-muted" : "text-foreground hover:bg-muted"}`}>Service Area</Link>
              <Link to="/about-us" onClick={() => setOpen(false)} className={`px-4 py-3 rounded-xl font-bold text-base transition-all ${isActive("/about-us") ? "bg-primary/10 text-primary" : dark ? "text-white hover:bg-muted" : "text-foreground hover:bg-muted"}`}>About Us</Link>
                <div className="border-t border-border mt-4 pt-4 flex flex-col gap-3">
                  <a href="tel:18329704583" className="flex items-center justify-center gap-2 bg-primary text-primary-foreground font-bold px-4 py-3.5 rounded-xl text-base shadow-md active:scale-95 transition-transform">
                    <Phone className="w-5 h-5" /> Call: 832 970 4583
                  </a>
                  <button
                    onClick={toggle}
                    className="flex items-center justify-center gap-2 border border-border rounded-xl px-4 py-3 font-bold text-base text-foreground hover:bg-muted transition-all"
                  >
                    {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                    {dark ? "Light Mode" : "Dark Mode"}
                  </button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}